import { useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import Link from 'next/link'

import {
  Box,
  UserMenu,
  useDisclosure,
  userMenuNameStyles,
  userMenuRoleStyles,
} from '@hivewatch/dark'

import { CreateUserFeedbackModal } from '@/features/feedback-crud'
import { getRoleDisplayName } from '@/features/managed-accounts'
import { useAppSelector } from '@/redux/hooks'
import { selectFullName, selectRoles } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import { useProfileMenuItems } from './ProfileMenuActions'
import { SideBarProfileCard } from './SideBarProfileCard'
import { PROFILE_ANALYTICS } from './constants'

export const SideBarProfile = () => {
  const colorScheme = useLightMode()
  const fullName = useAppSelector(selectFullName)
  const roles = useAppSelector(selectRoles)
  const { open: isOpen, onOpen, onClose } = useDisclosure()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)

  const roleDisplayNames = roles
    .map((role) => getRoleDisplayName(role))
    .filter(Boolean)
    .join(', ')

  const items = useProfileMenuItems({
    colorScheme,
    onSendFeedback: () => setIsFeedbackModalOpen(true),
  })

  const handleOpen = () => {
    trackEvent(PROFILE_ANALYTICS.openedPopover)
    onOpen()
  }

  const handleClose = () => {
    trackEvent(PROFILE_ANALYTICS.closedPopover)
    onClose()
  }

  const handleTriggerActivate = (toggle: () => void) => () => {
    trackEvent(PROFILE_ANALYTICS.profileCard)
    toggle()
  }

  return (
    <>
      <UserMenu
        colorScheme={colorScheme}
        data-testid='SideBarProfileMenu:popover'
        header={
          <>
            <Box {...userMenuNameStyles(colorScheme)} lineClamp={1}>
              {fullName}
            </Box>
            <Box {...userMenuRoleStyles(colorScheme)} lineClamp={2}>
              {roleDisplayNames}
            </Box>
          </>
        }
        // The feedback modal is launched from a menu row and renders above the
        // menu; without this the click landing in the modal would dismiss the
        // menu behind it.
        isDismissSuppressed={isFeedbackModalOpen}
        isOpen={isOpen}
        items={items}
        onClose={handleClose}
        onOpen={handleOpen}
        renderItem={(item, row) =>
          item.path ? (
            <Link href={item.path} legacyBehavior passHref>
              {row}
            </Link>
          ) : (
            row
          )
        }
        submenuTestId='HelpPopover:content'
        trigger={({ onClick, onKeyDown, isOpen: _isMenuOpen, ...menuAria }) => (
          <Box
            onClick={handleTriggerActivate(onClick)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                trackEvent(PROFILE_ANALYTICS.profileCard)
              }
              onKeyDown(event)
            }}
          >
            <SideBarProfileCard {...menuAria} colorScheme={colorScheme} />
          </Box>
        )}
      />
      {isFeedbackModalOpen && (
        <CreateUserFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
        />
      )}
    </>
  )
}
